import React from 'react';
import { useRippleEffect } from '../hooks/useRippleEffect';
import './Solutions.css';

const solutions = [
  {
    icon: (
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
        {/* Target with campaign elements */}
        <circle cx="40" cy="40" r="35" fill="none" stroke="#2f80a3" strokeWidth="2"/>
        <circle cx="40" cy="40" r="25" fill="none" stroke="#3ab7c3" strokeWidth="2"/>
        <circle cx="40" cy="40" r="15" fill="none" stroke="#22588e" strokeWidth="2"/>
        <circle cx="40" cy="40" r="6" fill="#1e2d7e"/>
        {/* Campaign message bubbles */}
        <ellipse cx="25" cy="20" rx="12" ry="8" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1"/>
        <ellipse cx="55" cy="25" rx="10" ry="6" fill="#10b981" stroke="#059669" strokeWidth="1"/>
        <ellipse cx="60" cy="55" rx="11" ry="7" fill="#e74c3c" stroke="#c0392b" strokeWidth="1"/>
        {/* Arrow pointing to center */}
        <path d="M25 30 L35 35" stroke="#1a2332" strokeWidth="2" strokeLinecap="round"/>
        <path d="M55 35 L45 38" stroke="#1a2332" strokeWidth="2" strokeLinecap="round"/>
        <path d="M55 50 L45 45" stroke="#1a2332" strokeWidth="2" strokeLinecap="round"/>
        {/* ROI indicator */}
        <text x="15" y="65" fill="#10b981" fontSize="12" fontWeight="bold">+50%</text>
      </svg>
    ),
    title: "Align Marketing Campaigns",
    description: "Increase campaign ROI with marketing messages grounded in real customer needs, not assumptions or demographics.",
    features: []
  },
  {
    icon: (
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
        {/* Ascending graph/chart */}
        <path d="M10 65 L10 15 M10 65 L70 65" stroke="#6b7280" strokeWidth="2"/>
        {/* Bars showing increasing conversion */}
        <rect x="15" y="50" width="8" height="15" fill="#3ab7c3"/>
        <rect x="30" y="40" width="8" height="25" fill="#2f80a3"/>
        <rect x="45" y="25" width="8" height="40" fill="#22588e"/>
        <rect x="60" y="15" width="8" height="50" fill="#1e2d7e"/>
        {/* Percentage symbol */}
        <circle cx="25" cy="25" r="3" fill="#10b981"/>
        <path d="M20 30 L35 15" stroke="#10b981" strokeWidth="2"/>
        <circle cx="35" cy="20" r="3" fill="#10b981"/>
        <text x="45" y="12" fill="#10b981" fontSize="12" fontWeight="bold">35%</text>
      </svg>
    ),
    title: "Increase Sales Conversion",
    description: "Boost conversion rates with precise customer insights that help your team speak directly to what customers want most.",
    features: []
  },
  {
    icon: (
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
        {/* Clock/timer with arrow */}
        <circle cx="40" cy="40" r="30" fill="none" stroke="#2f80a3" strokeWidth="3"/>
        <circle cx="40" cy="40" r="25" fill="none" stroke="#3ab7c3" strokeWidth="2"/>
        {/* Clock hands */}
        <path d="M40 40 L40 20" stroke="#1e2d7e" strokeWidth="3" strokeLinecap="round"/>
        <path d="M40 40 L55 40" stroke="#1e2d7e" strokeWidth="2" strokeLinecap="round"/>
        {/* Curved arrow showing reduction */}
        <path d="M25 15 Q15 25 25 35" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round"/>
        <path d="M20 30 L25 35 L30 30" fill="#10b981"/>
        <text x="15" y="12" fill="#10b981" fontSize="12" fontWeight="bold">-40%</text>
      </svg>
    ),
    title: "Reduce Sales Cycle Time",
    description: "Cut average sales cycle time by knowing exactly what customers need before your first conversation, eliminating guesswork.",
    features: []
  }
];

export const Solutions: React.FC = () => {
  const { ripples, createRipple } = useRippleEffect();

  return (
    <section id="solutions" className="solutions" onClick={createRipple}>
      <div className="solutions-container">
        <div className="solutions-header">
          <h2 className="section-title">
            <span className="title-highlight">Turn Customer Discovery Into Revenue Growth</span>
          </h2>
          <p className="section-description">
            Companies using <span className="spotton-brand-text">Spott-on</span> can see measurable improvements in sales performance, customer satisfaction, and revenue growth within the first quarter of its use.
          </p>
        </div>

        <div className="solutions-grid">
          {solutions.map((solution, index) => (
            <div key={index} className="solution-card">
              <div className="card-icon">{solution.icon}</div>
              <div className="card-content">
                <h3 className="card-title">{solution.title}</h3>
                <p className="card-description">{solution.description}</p>
              </div>
              {solution.features.length > 0 && (
                <ul className="card-features">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                          d="M6 8l2 2 4-4" 
                          stroke="#3ab7c3" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
              {solution.features.length > 0 && (
                <button className="card-cta">
                  Learn More
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M3 8h10m-5-5l5 5-5 5" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Ripple effects */}
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="section-ripple"
          style={{
            left: ripple.x,
            top: ripple.y,
          }}
        >
          <div className="ripple-ring ring-1"></div>
          <div className="ripple-ring ring-2"></div>
          <div className="ripple-ring ring-3"></div>
        </div>
      ))}
    </section>
  );
};